import { useState } from 'react';
import { Loader2, Radar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const VERDICT_META = {
  genuinely_active_and_counted: { variant: 'destructive', label: 'active + counted' },
  genuinely_active_but_hidden: { variant: 'destructive', label: 'active but HIDDEN (bug)' },
  stale_cancelled: { variant: 'outline', label: 'stale cancelled' },
  cancelled_in_hostaway_only: { variant: 'outline', label: 'cancelled in Hostaway only' },
  active_outside_trace_window: { variant: 'secondary', label: 'outside trace window (not a bug)' },
  not_found_in_db: { variant: 'outline', label: 'not found' },
};

function VerdictBadge({ verdict }) {
  const meta = VERDICT_META[verdict];
  return (
    <Badge variant={(meta && meta.variant) || 'outline'}>
      {(meta && meta.label) || verdict}
    </Badge>
  );
}

function predicateFailReasons(row) {
  if (!row.predicates) return [];
  const canonical = row.predicates.canonical_property_resolution;
  return [
    ...(row.predicates.booking_status_filter?.fail_reasons || []),
    ...(row.predicates.portfolio_dataset_inclusion_filter?.fail_reasons || []),
    ...(canonical && !canonical.resolves && canonical.note ? [canonical.note] : []),
    ...(row.predicates.bookings_tab_endpoint_query?.fail_reasons || []),
  ];
}

export function SmoobuReservationTracePanel() {
  const [smoobuIds, setSmoobuIds] = useState('');
  const [liveCheck, setLiveCheck] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (useFullLeakageList) => {
    setLoading(true);
    setResult(null);
    try {
      const params = { live_check: liveCheck };
      if (!useFullLeakageList && smoobuIds.trim()) {
        params.smoobu_ids = smoobuIds.trim();
      }
      const { data } = await diagnosticsAPI.smoobuReservationTrace(params);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const rows = (r && r.results) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Radar className="h-5 w-5 text-blue-600" />
          Smoobu Reservation Trace (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          For one or more Smoobu reservation ids, dumps the raw doc's full state and evaluates it
          against every Bookings-tab display predicate (booking_status_filter, portfolio
          inclusion, canonical property resolution, the dataset endpoint's own query), then
          cross-checks a live Smoobu API status and stored Hostaway reservations to explain why a
          hidden reservation is hidden. Leave the id field empty to trace every id the Migration
          Audit leakage card currently lists. Read-only, no writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[280px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Smoobu reservation id(s), comma-separated
            </label>
            <Input
              value={smoobuIds}
              onChange={(e) => setSmoobuIds(e.target.value)}
              placeholder="e.g. 132104422, 138823252, 124874026"
              className="font-mono"
            />
          </div>
          <label className="flex items-center gap-1 text-xs text-muted-foreground pb-2">
            <input
              type="checkbox"
              checked={liveCheck}
              onChange={(e) => setLiveCheck(e.target.checked)}
            />
            live Smoobu check
          </label>
          <Button
            onClick={() => run(false)}
            disabled={loading || !smoobuIds.trim()}
            data-testid="smoobu-trace-run"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Radar className="h-4 w-4 mr-2" />}
            Trace
          </Button>
          <Button
            variant="outline"
            onClick={() => run(true)}
            disabled={loading}
            data-testid="smoobu-trace-run-full-leakage"
          >
            Trace full leakage list
          </Button>
        </div>

        {r && (
          <div className="space-y-3" data-testid="smoobu-trace-result">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
              <Badge variant="outline">{r.source}</Badge>
              <Badge variant="outline">{r.traced_count} traced</Badge>
              <Badge variant={r.aggregate.would_appear_on_bookings_tab_count > 0 ? 'destructive' : 'outline'}>
                {r.aggregate.would_appear_on_bookings_tab_count} would appear on Bookings tab
              </Badge>
              <Badge variant="outline">{r.aggregate.hidden_from_bookings_tab_count} hidden</Badge>
              {r.aggregate.not_found_count > 0 && (
                <Badge variant="destructive">{r.aggregate.not_found_count} not found</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{r.aggregate.note}</p>

            {Object.keys(r.aggregate.verdict_counts || {}).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(r.aggregate.verdict_counts).map(([verdict, count]) => {
                  const meta = VERDICT_META[verdict];
                  return (
                    <Badge key={verdict} variant={(meta && meta.variant) || 'outline'}>
                      {(meta && meta.label) || verdict}: {count}
                    </Badge>
                  );
                })}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs border">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2">Smoobu id</th>
                    <th className="text-left p-2">Guest / dates</th>
                    <th className="text-left p-2">Status (stored / live / effective)</th>
                    <th className="text-center p-2">Would appear</th>
                    <th className="text-left p-2">Fail reasons</th>
                    <th className="text-left p-2">Hostaway cross-check</th>
                    <th className="text-left p-2">Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.smoobu_id} className="border-t align-top" data-testid="smoobu-trace-row">
                      <td className="p-2 font-mono">{row.smoobu_id}</td>
                      <td className="p-2">
                        {row.raw_doc ? (
                          <>
                            {row.raw_doc.guest_name || <em>(empty)</em>}
                            <div className="font-mono text-muted-foreground">
                              {row.raw_doc.check_in}&rarr;{row.raw_doc.check_out}
                            </div>
                          </>
                        ) : (
                          <em>no document</em>
                        )}
                      </td>
                      <td className="p-2 font-mono">
                        {row.raw_doc ? row.raw_doc.status : '—'}
                        {' / '}
                        {row.live_smoobu_check && row.live_smoobu_check.attempted
                          ? (row.live_smoobu_check.live_status_inferred || `error: ${row.live_smoobu_check.error}`)
                          : 'not checked'}
                        {' / '}
                        {row.effective_status || '—'}
                      </td>
                      <td className="p-2 text-center">
                        {row.found_in_db ? (row.would_appear_on_bookings_tab ? 'yes' : 'no') : '—'}
                      </td>
                      <td className="p-2">
                        {predicateFailReasons(row).length === 0 ? '—' : predicateFailReasons(row).join(', ')}
                      </td>
                      <td className="p-2">
                        {row.hostaway_cross_check && row.hostaway_cross_check.best_match ? (
                          <span>
                            {row.hostaway_cross_check.best_match.hostaway_id} (
                            {row.hostaway_cross_check.best_match.status})
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="p-2"><VerdictBadge verdict={row.verdict} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
